/* eslint-disable no-unsafe-optional-chaining */
import { api, endpoint } from "@/api";
import appConfig from "@/config/appConfig";
import { userRole } from "@/constant";
import { DeleteOutlined } from "@ant-design/icons";
import { Button, Checkbox, Form, Input, InputNumber, Select, Spin, TimePicker } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { AiOutlinePlus } from "react-icons/ai";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import AlarmGraph from "./AlarmGraph";
import { flare } from "@flareapp/js";

const AlarmForm = ({ mode = "create", alarmData = {} }) => {
  const navigate = useNavigate();
  const { userDetails } = useSelector((state) => state?.user);
  const [form] = Form.useForm();

  const [planDetails, setPlanDetails] = useState({});
  const [limitations, setLimitations] = useState({});
  const [businessesList, setBusinessesList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [siteLocations, setSiteLocations] = useState([]);
  const [deviceParameters, setDeviceParameters] = useState([]);
  const [devices, setDevices] = useState([]);
  const [formData, setFormData] = useState(
    alarmData || {
      name: "",
      type: "email",
      settings: "single",
      site: [],
      device: [],
      parameter: [],
      option: "min",
      max: "",
      min: "",
      interval: "",
      // critical: "",
      // warning: "",
      business: null,
    },
  );
  const { site, device, option, business, settings, isContinuous } = formData;

  const getSiteLocationList = async (business) => {
    try {
      const { data } = await api.get(`/site-location`, { params: { business, limit: 100000 } });
      setSiteLocations(data);
    } catch {
      setSiteLocations([]);
    }
  };

  const getBusiness = async () => {
    try {
      const data = await api.get(endpoint.business);
      setBusinessesList(data?.data);
      if (Array.isArray(data?.data) && data.data.length === 1) {
        setFormData({ ...formData, business: data.data[0]._id });
        form.setFieldsValue({ business: data.data[0]._id });
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log("business list", error);
    }
  };

  const getMySubscription = async () => {
    try {
      const { result } = await api.get(endpoint.mySubscription, {
        params: {
          business,
          unknown: true,
        },
      });
      setPlanDetails(result?.[0]?.assignedPlan);
      setLimitations(result?.[0]);
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log("my subscription", error);
    }
  };
  const isLimitOver =
    userDetails?.role !== userRole.superAdmin && planDetails?.locationCount <= limitations?.siteLocation?.length;

  useEffect(() => {
    if (mode === "edit") {
      document.title = `${appConfig.name} - Edit Alarm `;
      const today = dayjs().format("YYYY-MM-DD"); // Get today's date in YYYY-MM-DD format
      const updated = () => {
        const body = { ...formData, ...alarmData };
        if (alarmData?.condition === "time") {
          body.timeRange = alarmData?.timeRange?.map((time) => dayjs(`${today} ${time}`, "YYYY-MM-DD HH:mm"));
        }
        if (alarmData?.condition === "dayAndTime") {
          // const { days = [] } = body?.dayTime || {};
          // console.log({ days, body: body?.dayTime });

          // const newTime = days?.reduce((acc, key) => {
          //   if (key !== "days" && body?.dayTime[key]) {
          //     acc[key] = body?.dayTime[key].map((v) => dayjs(`${today} ${v}`, "YYYY-MM-DD HH:mm"));
          //   }
          //   return acc;
          // }, {});
          // body.dayTime = { days, ...newTime };

          body.dayTime = Object.entries(body?.dayTime || {})?.reduce((acc, [day, ranges]) => {
            // console.log({ day, ranges });
            if (day === "days") acc[day] = ranges;
            if (day !== "days" && Array.isArray(ranges)) {
              acc[day] = ranges.map((v) => {
                if (v?.start && v?.end) {
                  return {
                    range: [
                      dayjs(`${today} ${v?.start}`, "YYYY-MM-DD HH:mm"),
                      dayjs(`${today} ${v?.end}`, "YYYY-MM-DD HH:mm"),
                    ],
                  };
                } else {
                  return [];
                }
              });
            }
            // console.log({ acc });
            return acc;
          }, {});
        }
        // console.log({ body });
        return body;
      };
      form.setFieldsValue(updated());

      setFormData(updated());
    } else {
      document.title = `${appConfig.name} - Create New Alarm`;
    }
  }, [mode]);

  useEffect(() => {
    if (business) {
      getSiteLocationList(business);
      getMySubscription();
    }
    setFormData({ ...formData, device: [], site: [] });
    setDevices([]);
  }, [userDetails, business]);

  useEffect(() => {
    getBusiness();
  }, []);

  const getDevices = async (locationId) => {
    const response = await axios.get(`/device`, {
      withCredentials: true,
      params: { site: locationId, unknown: true },
    });
    if (response) {
      setDevices(response.data.results);
    }
  };

  useEffect(() => {
    if (site?.length) {
      getDevices(site);
    }
  }, [site]);

  const getDeviceParameters = async (deviceId) => {
    try {
      const { results } = await api.get(endpoint.get_device_parameter, {
        params: {
          deviceId,
          limit: 100000,
          page: 1,
          sortBy: "name",
          orderBy: "ASC",
          type: "formula",
        },
      });
      setDeviceParameters(results);
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log("device parameters", error);
    }
  };

  useEffect(() => {
    if (device?.length) {
      getDeviceParameters(device);
    }
  }, [device]);

  const createAlarm = async (values) => {
    setIsLoading(true);

    try {
      // eslint-disable-next-line no-unused-vars
      const { business, ...dd } = values;
      await api.post(endpoint.notification, { ...dd });
      navigate("/alarm-summary");
      await Swal.fire({
        icon: "success",
        text: "Notification has been created.",
        timer: 1000,
        confirmButtonText: "OK",
      });
    } catch {
      await Swal.fire({
        icon: "error",
        text: "Something wen wrong",
        timer: 1000,
        confirmButtonText: "OK",
      });
    } finally {
      setIsLoading(false);
    }
  };
  const updateAlarm = async (payload) => {
    setIsLoading(true);

    try {
      // eslint-disable-next-line no-unused-vars
      const { business, ...dd } = payload;
      await api.put(`${endpoint.notification}/${alarmData?._id}`, { ...dd });
      await Swal.fire({
        icon: "success",
        text: "Notification has been updated.",
        timer: 2000,
        confirmButtonText: "OK",
      });
      navigate("/alarm-summary");
    } catch {
      await Swal.fire({
        icon: "error",
        text: "Something wen wrong",
        timer: 2000,
        confirmButtonText: "OK",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // antd
  const onFinish = async (values) => {
    // return console.log({ values });
    const { condition, timeRange } = values;
    const body = {
      ...values,
      min: Number(values?.min) || 0,
      max: Number(values?.max) || 0,
      interval: Number(values?.interval) || 0,
      critical: Number(values?.critical) || 0,
      warning: Number(values?.warning) || 0,
    };

    if (condition === "time") {
      body.timeRange = timeRange?.map((time) => time?.format("HH:mm"));
    }
    if (condition === "dayAndTime") {
      const formatted = Object.fromEntries(
        Object.entries(values.dayTime || {}).map(([day, ranges]) => {
          if (day === "days") return [day, ranges];
          return [
            day,
            ranges.map(({ range }) => {
              if (range) {
                return {
                  start: range?.[0]?.format("HH:mm") || "",
                  end: range?.[1]?.format("HH:mm") || "",
                };
              }
            }),
          ];
        }),
      );

      body.dayTime = { ...formatted };
    }
    if (mode === "create") {
      await createAlarm(body);
    } else if (mode === "edit") {
      await updateAlarm(body);
    }
    setIsLoading(false);
  };
  const onFinishFailed = (errorInfo) => {
    // eslint-disable-next-line no-console
    console.log("Failed:", errorInfo);
  };

  return (
    <div className="alarm-page">
      <div className="card p-3 m-md-3">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <label className="az-content-label">Manage Alarm</label>
            <span className="d-block py-2">{mode === "edit" ? "Edit Alarm Settings" : "Create New Alarm"}</span>
          </div>
        </div>
        <hr className="mt-0" />
        <Spin spinning={isLoading}>
          <Form
            layout="vertical"
            name="alarm"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            form={form}
            scrollToFirstError={true}
            onValuesChange={(_, values) => {
              // console.log({ values });
              setFormData(values);
            }}
            // initialValues={
            //   mode === "edit"
            //     ? {
            //         ...formData,
            //         days: [],
            //         dayTime: { days: [], timeRange: {} },
            //         timeRange: [],
            //       }
            //     : {}
            //   // : { option: "min", type: "email", settings: "single" }
            // }
          >
            <Row>
              <Col md={6}>
                <Form.Item
                  label="Alarm Name                  "
                  name="name"
                  rules={[{ required: true, message: "Enter Alarm Name" }]}
                >
                  <Input placeholder="Enter Alarm Name" />
                </Form.Item>

                <Form.Item label="Alarm Type" name="type" rules={[{ required: true, message: "Select Alarm Type" }]}>
                  <Select
                    showSearch={true}
                    filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                    placeholder="Select Alarm Type"
                    options={[
                      { value: "email", label: "Email + System Notification" },
                      { value: "message", label: "Email + SMS + System Notification" },
                    ]}
                  />
                </Form.Item>
                <div>
                  <Form.Item
                    label="Business"
                    name="business"
                    rules={[{ required: true, message: "Select a Business" }]}
                    extra={
                      isLimitOver && (
                        <p
                          style={{
                            color: "red",
                            marginBottom: 0,
                            marginTop: "2px",
                          }}
                        >
                          Warning: {limitations?.name}({limitations?.id}) have exceeded the limit for the maximum number
                          of notifications.
                        </p>
                      )
                    }
                  >
                    <Select
                      showSearch={true}
                      className="w-100"
                      disabled={businessesList.length === 1}
                      onChange={(value) => {
                        form.setFieldsValue({
                          business: value,
                          site: undefined,
                          device: undefined,
                          parameter: undefined,
                        });
                      }}
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      placeholder="Select Business"
                      options={[
                        ...(businessesList?.map((item) => ({ label: `${item.name} (${item.id})`, value: item._id })) ||
                          []),
                      ]}
                    />
                  </Form.Item>
                </div>

                <Form.Item
                  label="Alarm Settings"
                  name="settings"
                  rules={[{ required: true, message: "Select Alarm Settings" }]}
                >
                  <Select
                    showSearch={true}
                    filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                    placeholder="Select Alarm Settings"
                    options={[
                      { value: "single", label: "Single Setting" },
                      { value: "master", label: "Master Setting" },
                    ]}
                    onChange={() => {
                      form.setFieldsValue({
                        site: undefined,
                        device: undefined,
                        parameter: undefined,
                      });
                    }}
                  />
                </Form.Item>

                <Form.Item
                  label="Site Location"
                  name="site"
                  rules={[{ required: true, message: "Select Site Location" }]}
                >
                  <Select
                    mode={settings === "master" ? "multiple" : null}
                    showSearch={true}
                    filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                    placeholder="Select Site Location"
                    onChange={(value) => {
                      form.setFieldsValue({
                        site: settings === "master" ? value : [value],
                        device: undefined,
                        parameter: undefined,
                      });
                    }}
                    options={[...(siteLocations?.map((item) => ({ value: item._id, label: item.name })) || [])]}
                  />
                </Form.Item>

                <Form.Item
                  label="Device"
                  name="device"
                  rules={[{ required: true, message: "Select Device" }]}
                  className="w-100"
                >
                  <Select
                    showSearch={true}
                    mode={settings === "master" ? "multiple" : null}
                    filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                    // filterOption={(input, option) =>
                    //   (option?.children ? option?.children?.toLowerCase() : "").includes(input?.toLowerCase())
                    // }
                    onChange={(value) => {
                      form.setFieldsValue({ device: settings === "master" ? value : [value], parameter: undefined });
                    }}
                    placeholder="Select Device"
                    maxTagCount={2}
                    options={[...devices?.map((item) => ({ value: item._id, label: item.name }))]}
                  />
                </Form.Item>
                {option === "offline" ? (
                  ""
                ) : (
                  <Form.Item
                    label="Device Parameter"
                    name="parameter"
                    rules={[{ required: true, message: "Select Device Parameter" }]}
                    className="w-100"
                  >
                    <Select
                      showSearch={true}
                      mode={settings === "master" ? "multiple" : null}
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      // filterOption={(input, option) =>
                      //   (option?.children ? option?.children.toLowerCase() : "").includes(input?.toLowerCase())
                      // }
                      onChange={(value) =>
                        form.setFieldsValue({
                          parameter: settings === "master" ? value : [value],
                        })
                      }
                      placeholder="Select Device Parameter"
                      maxTagCount={1}
                      maxTagTextLength={20}
                      options={[
                        ...deviceParameters?.map((item) => ({
                          value: item._id,
                          label: item?.device?.site?.name + " > " + item?.device?.name + " > " + item.name,
                        })),
                      ]}
                    />
                  </Form.Item>
                )}
              </Col>

              <Col md={6}>
                <Form.Item label=" Alarm Notification" name="option" className="w-100">
                  <Select
                    showSearch={true}
                    // filterOption={(input, option) =>
                    //   (option?.children ? option?.children.toLowerCase() : "").includes(input?.toLowerCase())
                    // }
                    filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                    placeholder="Select Alarm Notification"
                    maxTagCount={1}
                    maxTagTextLength={20}
                    options={[
                      { value: "min", label: "Min" },
                      { value: "max", label: "Max" },
                      { value: "range", label: "Range" },
                      { value: "offline", label: "Offline" },
                    ]}
                  />
                </Form.Item>

                <Form.Item label="" name="isContinuous" valuePropName="checked" className="w-100">
                  <Checkbox> Continuous Alarm </Checkbox>
                </Form.Item>

                {isContinuous && (
                  <Form.Item
                    label="Observation Time"
                    name="observationTime"
                    rules={[{ required: true, message: "Select Alarm Observation Time" }]}
                    className="w-100"
                  >
                    <Select
                      showSearch={true}
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      placeholder="Select Alarm Observation Time"
                      maxTagCount={1}
                      maxTagTextLength={20}
                      options={[
                        { value: 1, label: "1 min" },
                        { value: 15, label: "15 min" },
                        { value: 30, label: "30 min" },
                        { value: 60, label: "1 hour" },
                        { value: 720, label: "12 hours" },
                        { value: 1440, label: "24 hours" },
                      ]}
                    />
                  </Form.Item>
                )}

                <Row>
                  {["min", "range"].includes(option) && (
                    <Col md={6}>
                      <Form.Item
                        label={option === "range" ? "Start Value" : "Set Value"}
                        name="min"
                        rules={[{ required: true, message: "Enter minimum value to trigger alarm" }]}
                      >
                        <InputNumber className="w-100" placeholder="Enter minimum value to trigger alarm" />
                      </Form.Item>
                    </Col>
                  )}

                  {["max", "range"].includes(option) && (
                    <Col md={6}>
                      <Form.Item
                        label={option === "range" ? "End Value" : "Set Value"}
                        name="max"
                        rules={[{ required: true, message: "Enter maximum value to trigger alarm" }]}
                      >
                        <InputNumber className="w-100" placeholder="Enter maximum value to trigger alarm" />
                      </Form.Item>
                    </Col>
                  )}
                  <Col md={6}>
                    <Form.Item
                      label="Set Trigger Interval"
                      name="interval"
                      rules={[{ required: true, message: "Select Alarm Trigger Interval" }]}
                      className="w-100"
                    >
                      <Select
                        showSearch={true}
                        filterOption={(input, option) =>
                          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        placeholder="Select Alarm Trigger Interval"
                        maxTagCount={1}
                        maxTagTextLength={20}
                        options={[
                          { value: 1, label: "1 min" },
                          { value: 15, label: "15 min" },
                          { value: 30, label: "30 min" },
                          { value: 60, label: "1 hour" },
                          { value: 720, label: "12 hours" },
                          { value: 1440, label: "24 hours" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label="Time Trigger Condition"
                  name="condition"
                  rules={[{ required: true, message: "Select Time Trigger Condition" }]}
                  className="w-100"
                  initialValue=""
                >
                  <Select
                    showSearch
                    onChange={() => {
                      setFormData((pre) => ({ ...pre, days: [], dayTime: { days: [] }, timeRange: [] }));
                      form.setFieldsValue({ days: [], dayTime: { days: [] }, timeRange: [] });
                    }}
                    placeholder="Select Alarm Trigger Interval"
                    options={[
                      { value: "", label: "None" },
                      { value: "day", label: "Day" },
                      { value: "time", label: "Time" },
                      { value: "dayAndTime", label: "Day & Time" },
                    ]}
                  />
                </Form.Item>

                {/* Select Days */}
                {formData?.condition === "day" && (
                  <Form.Item
                    name="days"
                    label="Select Days"
                    rules={[{ required: true, message: "Please select at least one day!" }]}
                  >
                    <Checkbox.Group>
                      <Row xs={2} className="px-3 g-2">
                        {daysOfWeek.map((day) => (
                          <Col key={day}>
                            <Checkbox value={day}>{day}</Checkbox>
                          </Col>
                        ))}
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                )}

                {/* Select Time Range */}
                {formData?.condition === "time" && (
                  <Form.Item
                    name="timeRange"
                    label="Select Time Range"
                    rules={[{ required: true, message: "Please select a time range!" }]}
                  >
                    <TimePicker.RangePicker className="w-100" format="HH:mm" />
                  </Form.Item>
                )}

                {/* Select Specific Days with Time */}
                {formData?.condition === "dayAndTime" && (
                  <Form.Item label="Select Days & Time">
                    <Form.Item
                      name={["dayTime", "days"]}
                      rules={[{ required: true, message: "Please select at least one day" }]}
                    >
                      <Checkbox.Group className="d-block">
                        {daysOfWeek.map((day) => {
                          const isSelected = formData?.dayTime?.days?.includes(day);

                          return (
                            <div className="d-flex align-items-baseline w-100" key={day}>
                              <Checkbox
                                style={{ width: 150 }}
                                value={day}
                                // onChange={() => {
                                //   form.setFieldValue(["dayTime", day], []);
                                // }}
                              >
                                {day}
                              </Checkbox>

                              <Form.List name={["dayTime", day]} initialValue={[{}]}>
                                {(fields, { add, remove }) => (
                                  <div className="w-100">
                                    {fields.map(({ key, name, ...restField }, ind) => (
                                      <div key={key} className="d-flex align-items-baseline w-100 gap-3">
                                        <Form.Item
                                          {...restField}
                                          name={[name, "range"]}
                                          className="w-100"
                                          rules={[
                                            {
                                              required: Boolean(isSelected),
                                              message: "Please select time range!",
                                            },
                                          ]}
                                        >
                                          {/* <TimePicker.RangePicker
                                            format="HH:mm"
                                            disabled={!isSelected}
                                            className="w-100"
                                            disabledDate={(currentDate, info) => true}
                                            order={true}
                                          /> */}
                                          <TimePicker.RangePicker
                                            format="HH:mm"
                                            disabled={!isSelected}
                                            className="w-100"
                                            order={true}
                                            disabledTime={() => {
                                              const selectedRanges = form.getFieldValue(["dayTime", day]) || [];

                                              const disabledHoursSet = new Set();
                                              const disabledMinutesMap = {};

                                              selectedRanges.forEach((item, idx) => {
                                                if (!item?.range || idx === ind) return; // Skip current field

                                                const [start, end] = item.range;
                                                if (!start || !end) return;

                                                const startHour = start.hour();
                                                const endHour = end.hour();

                                                for (let h = startHour; h <= endHour; h++) {
                                                  disabledHoursSet.add(h);

                                                  const minuteSet = disabledMinutesMap[h] || new Set();
                                                  if (h === startHour && h === endHour) {
                                                    for (let m = start.minute(); m <= end.minute(); m++) {
                                                      minuteSet.add(m);
                                                    }
                                                  } else if (h === startHour) {
                                                    for (let m = start.minute(); m < 60; m++) {
                                                      minuteSet.add(m);
                                                    }
                                                  } else if (h === endHour) {
                                                    for (let m = 0; m <= end.minute(); m++) {
                                                      minuteSet.add(m);
                                                    }
                                                  } else {
                                                    for (let m = 0; m < 60; m++) {
                                                      minuteSet.add(m);
                                                    }
                                                  }
                                                  disabledMinutesMap[h] = minuteSet;
                                                }
                                              });

                                              return {
                                                disabledHours: () => Array.from(disabledHoursSet),
                                                disabledMinutes: (hour) =>
                                                  disabledMinutesMap[hour] ? Array.from(disabledMinutesMap[hour]) : [],
                                              };
                                            }}
                                          />
                                        </Form.Item>

                                        <Form.Item>
                                          {ind === 0 ? (
                                            <button
                                              type="button"
                                              onClick={() => add({})}
                                              className="btn btn-success rounded-circle"
                                              disabled={!isSelected}
                                            >
                                              <AiOutlinePlus />
                                            </button>
                                          ) : (
                                            <button
                                              type="button"
                                              onClick={() => remove(name)}
                                              className="btn btn-danger rounded-circle"
                                              disabled={!isSelected}
                                            >
                                              <DeleteOutlined />
                                            </button>
                                          )}
                                        </Form.Item>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </Form.List>
                            </div>
                          );
                        })}
                      </Checkbox.Group>
                    </Form.Item>
                  </Form.Item>
                )}
              </Col>
            </Row>

            <AlarmGraph data={formData} deviceParameters={deviceParameters} />

            <div className="d-flex align-items-center justify-content-end gap-2 pt-2">
              <Button type="primary" htmlType="submit">
                {mode === "edit" ? "Update" : "Create"} Alarm
              </Button>
              <Button type="primary" className="bg-secondary" onClick={() => navigate(-1)} htmlType="button">
                Cancel
              </Button>
            </div>
          </Form>
        </Spin>
      </div>
    </div>
  );
};

export default AlarmForm;

const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

AlarmForm.propTypes = {
  mode: PropTypes.string,
  alarmData: PropTypes.object,
};
